import React, { useState, useEffect } from "react";
import classes from "./EmployeeLoanModal.module.css";
import { X } from "lucide-react";
import Select from "react-select";
import api, { withCompanyId } from "../../../Components/API/Api";
import Swal from "sweetalert2";

function EmployeeLoanModal({ isOpen, onClose }) {
    const [formData, setFormData] = useState({
        employeeName: "",
        employeeId: "",
        employeeIdentify: "",
        department: "",
        position: "",
        loanAmount: "",
        interestRate: "5",
        tenure: "",
        startDate: "",
        loanType: "",
        bankName: "",
        comment: "",
    });

    const [summary, setSummary] = useState({
        monthly: 0,
        total: 0,
    });

    const [employees, setEmployees] = useState([]);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [loadingEmployees, setLoadingEmployees] = useState(false);

    const [loanTypes, setLoanTypes] = useState([]);
    const [loanTypeOptions, setLoanTypeOptions] = useState([]);
    const [loadingLoanTypes, setLoadingLoanTypes] = useState(false);

    const [bankAccounts, setBankAccounts] = useState([]);
    const [bankAccountOptions, setBankAccountOptions] = useState([]);
    const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    // Fetch employees, loan types, and bank accounts on component mount
    useEffect(() => {
        if (isOpen) {
            fetchEmployees();
            fetchLoanTypes();
            fetchBankAccounts();
        }
    }, [isOpen]);

    const fetchEmployees = async () => {
        try {
            setLoadingEmployees(true);
            const response = await api.get("/v1/employee/paginated");

            // Handle paginated response: { status, message, data: { current_page, data: [...], ... } }
            if (response.data.status && response.data.data?.data) {
                const empData = response.data.data.data;
                setEmployees(empData);

                // Format for react-select
                const options = empData.map((emp) => ({
                    value: emp.id,
                    label: emp.first_name + " " + (emp.last_name || ""),
                    data: emp,
                }));
                setEmployeeOptions(options);
            }
        } catch (error) {
            console.error("Failed to fetch employees:", error);
        } finally {
            setLoadingEmployees(false);
        }
    };

    const fetchLoanTypes = async () => {
        try {
            setLoadingLoanTypes(true);
            const response = await api.get("/v1/loantype");

            if (response.data.status) {
                const loanTypeData = response.data.data;
                setLoanTypes(loanTypeData);

                // Format for react-select
                const options = loanTypeData.map((type) => ({
                    value: type.id,
                    label: type.description,
                    data: type,
                }));
                setLoanTypeOptions(options);
            }
        } catch (error) {
            console.error("Failed to fetch loan types:", error);
        } finally {
            setLoadingLoanTypes(false);
        }
    };

    const fetchBankAccounts = async () => {
        try {
            setLoadingBankAccounts(true);
            const response = await api.get("/v1/bank-accounts");

            if (response.data.status) {
                const bankData = response.data.data;
                setBankAccounts(bankData);

                // Format for react-select
                const options = bankData.map((bank) => ({
                    value: bank.id,
                    label: bank.gl_name,
                    data: bank,
                }));
                setBankAccountOptions(options);
            }
        } catch (error) {
            console.error("Failed to fetch bank accounts:", error);
        } finally {
            setLoadingBankAccounts(false);
        }
    };

    if (!isOpen) return null;

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Recalculate if any loan-related field changed
        if (["loanAmount", "interestRate", "tenure"].includes(name)) {
            calculateLoan({ ...formData, [name]: value });
        }
    };

    // Handle employee selection
    const handleEmployeeChange = (option) => {
        if (option) {
            const employee = option.data;
            setFormData((prev) => ({
                ...prev,
                employeeName: employee.name,
                employeeId: employee.id,
                employeeIdentify: employee.employee_id,
                department: employee.department || "",
                position: employee.position || "",
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                employeeName: "",
                employeeId: "",
                employeeIdentify: "",
                department: "",
                position: "",
            }));
        }
    };

    // Handle loan type selection
    const handleLoanTypeChange = (option) => {
        if (option) {
            setFormData((prev) => ({
                ...prev,
                loanType: option.value,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                loanType: "",
            }));
        }
    };

    // Handle bank name selection
    const handleBankNameChange = (option) => {
        if (option) {
            setFormData((prev) => ({
                ...prev,
                bankName: option.value,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                bankName: "",
            }));
        }
    };

    // Calculate loan details
    const calculateLoan = (data) => {
        const amount = parseFloat(data.loanAmount) || 0;
        const rate = parseFloat(data.interestRate) || 0;
        const months = parseFloat(data.tenure) || 0;

        const interest = (amount * rate) / 100;
        const total = amount + interest;
        const monthly = months ? total / months : 0;

        setSummary({
            monthly: monthly.toFixed(2),
            total: total.toFixed(2),
        });
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.employeeId || !formData.loanType) {
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text: "Please select an employee and loan type.",
            });
            return;
        }

        if (!formData.loanAmount || !formData.tenure) {
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text: "Please enter loan amount and tenure.",
            });
            return;
        }

        setSubmitting(true);

        try {
            const payload = {
                employee_id: formData.employeeId,
                loan_type: formData.loanType,
                loan_amount: formData.loanAmount,
                tenure: formData.tenure,
                start_date: formData.startDate,
                bank: formData.bankName,
                comment: formData.comment,
                total_loan: summary.total,
                monthly_deduction: summary.monthly,
            };

            const body = withCompanyId(payload);
            const response = await api.post("/v1/employeeloan/create", body);

            Swal.fire({
                icon: "success",
                title: "Loan Request Submitted",
                text: response.data.message || "Employee loan application submitted successfully!",
                timer: 2000,
                showConfirmButton: false,
                didOpen: () => {
                    const popup = Swal.getPopup();
                    const backdrop = document.querySelector(".swal2-container");
                    if (popup) {
                        popup.style.zIndex = "999999";
                    }
                    if (backdrop) {
                        backdrop.style.zIndex = "999998";
                    }
                },
            });

            // Reset form after successful submission
            setFormData({
                employeeName: "",
                employeeId: "",
                employeeIdentify: "",
                department: "",
                position: "",
                loanAmount: "",
                interestRate: "5",
                tenure: "",
                startDate: "",
                loanType: "",
                bankName: "",
                comment: "",
            });

            setSummary({
                monthly: 0,
                total: 0,
            });

            // Close modal
            setTimeout(() => {
                onClose();
            }, 2000);
        } catch (error) {
            let errorMessage = "Failed to submit loan application.";

            if (error.response?.data?.message) {
                // Check if message is an object (field-specific errors)
                if (typeof error.response.data.message === "object") {
                    // Format object errors to readable string
                    errorMessage = Object.entries(error.response.data.message)
                        .map(([field, messages]) => {
                            const fieldLabel = field
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (char) => char.toUpperCase());
                            return `${fieldLabel}: ${Array.isArray(messages) ? messages.join(", ") : messages}`;
                        })
                        .join("\n");
                } else {
                    // String error message
                    errorMessage = error.response.data.message;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            Swal.fire({
                icon: "error",
                title: "Submission Failed",
                text: errorMessage,
                allowOutsideClick: true,
                allowEscapeKey: true,
                didOpen: () => {
                    const popup = Swal.getPopup();
                    const backdrop = document.querySelector(".swal2-container");
                    if (popup) {
                        popup.style.zIndex = "999999";
                    }
                    if (backdrop) {
                        backdrop.style.zIndex = "999998";
                    }
                },
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={classes.modalOverlay}>
            <div className={classes.modalContent}>
                <div className={classes.modalHeader}>
                    <h2 className={classes.modalTitle}>Employee Loan Application</h2>
                    <button
                        className={classes.closeButton}
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className={classes.modalBody}>
                    <div className={classes.subtitle}>
                        Fill in the details below to request a loan
                    </div>

                    <form id="loanForm" onSubmit={handleSubmit}>
                        {/* Employee Info */}
                        <div className={classes.section}>Employee Information</div>

                        <div className={classes.formGrid}>
                            <div className={classes.formGroup}>
                                <label htmlFor="employeeName">Employee Name</label>
                                <Select
                                    id="employeeName"
                                    options={employeeOptions}
                                    isLoading={loadingEmployees}
                                    placeholder="Select employee"
                                    isClearable
                                    value={
                                        employeeOptions.find(
                                            (opt) => opt.value === formData.employeeId
                                        ) || null
                                    }
                                    onChange={handleEmployeeChange}
                                />
                            </div>

                            <div className={classes.formGroup}>
                                <label htmlFor="employeeIdentify">Employee ID</label>
                                <input
                                    type="text"
                                    id="employeeIdentify"
                                    name="employeeIdentify"
                                    placeholder="EMP001"
                                    value={formData.employeeIdentify}
                                    onChange={handleChange}
                                    disabled
                                />
                            </div>

                            <div className={classes.formGroup}>
                                <label htmlFor="department">Department</label>
                                <input
                                    type="text"
                                    id="department"
                                    name="department"
                                    placeholder="Finance"
                                    value={formData.department}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className={classes.formGroup}>
                                <label htmlFor="position">Position</label>
                                <input
                                    type="text"
                                    id="position"
                                    name="position"
                                    placeholder="Accountant"
                                    value={formData.position}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        {/* Loan Info */}
                        <div className={classes.section}>Loan Details</div>

                        <div className={classes.formGrid}>
                            <div className={classes.formGroup}>
                                <label htmlFor="loanAmount">Loan Amount (₦)</label>
                                <input
                                    type="number"
                                    id="loanAmount"
                                    name="loanAmount"
                                    placeholder="0"
                                    value={formData.loanAmount}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className={classes.formGroup}>
                                <label htmlFor="interestRate">Interest Rate (%)</label>
                                <input
                                    type="number"
                                    id="interestRate"
                                    name="interestRate"
                                    placeholder="5"
                                    value={formData.interestRate}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className={classes.formGroup}>
                                <label htmlFor="tenure">Tenure (Months)</label>
                                <input
                                    type="number"
                                    id="tenure"
                                    name="tenure"
                                    placeholder="0"
                                    value={formData.tenure}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className={classes.formGroup}>
                                <label htmlFor="startDate">Start Date</label>
                                <input
                                    type="date"
                                    id="startDate"
                                    name="startDate"
                                    value={formData.startDate}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        {/* Loan Summary - Full Width */}
                        <div className={classes.section}>Loan Summary</div>

                        <div className={classes.preview}>
                            <p>
                                Monthly Deduction:{" "}
                                <span className={classes.highlight}>₦{summary.monthly}</span>
                            </p>
                            <p>
                                Total Repayment:{" "}
                                <span className={classes.highlight}>₦{summary.total}</span>
                            </p>
                        </div>

                        {/* Loan Type and Bank Name - Side by Side */}
                        <div className={classes.formGrid}>
                            <div className={classes.formGroup}>
                                <label htmlFor="loanType">Loan Type</label>
                                <Select
                                    id="loanType"
                                    options={loanTypeOptions}
                                    isLoading={loadingLoanTypes}
                                    placeholder="Select loan type"
                                    isClearable
                                    value={
                                        loanTypeOptions.find(
                                            (opt) => opt.value === formData.loanType
                                        ) || null
                                    }
                                    onChange={handleLoanTypeChange}
                                />
                            </div>

                            <div className={classes.formGroup}>
                                <label htmlFor="bankName">Bank Name</label>
                                <Select
                                    id="bankName"
                                    options={bankAccountOptions}
                                    isLoading={loadingBankAccounts}
                                    placeholder="Select bank account"
                                    isClearable
                                    value={
                                        bankAccountOptions.find(
                                            (opt) => opt.value === formData.bankName
                                        ) || null
                                    }
                                    onChange={handleBankNameChange}
                                />
                            </div>
                        </div>

                        {/* Reason for Loan - Full Width */}
                        <div className={classes.formGrid}>
                            <div className={`${classes.formGroup} ${classes.full}`}>
                                <label htmlFor="comment">Reason for Loan</label>
                                <textarea
                                    id="comment"
                                    name="comment"
                                    rows="3"
                                    placeholder="Enter reason for loan"
                                    value={formData.comment}
                                    onChange={handleChange}
                                ></textarea>
                            </div>
                        </div>



                        <button
                            type="submit"
                            className={classes.submitButton}
                            disabled={submitting}
                        >
                            {submitting ? "Submitting..." : "Submit Loan Request"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default EmployeeLoanModal;
